import type { FC } from 'react'
import CloseIcon from '../../assets/images/close_rg.svg'
import { Button } from '../Buttons/Button'
import type { PlanStepDraft } from '../../features/chargingSteering/chargingSteeringSlice'
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator'
import type { AllowedChargingPower } from '../../types/charging-steering.types'
import { formatAllowedChargingPower } from '../../features/chargingSteering/utils/chargingPlan.utils'

type ChargingPlanEditorProps = {
  start: string
  steps: PlanStepDraft[]
  allowedChargingPower?: AllowedChargingPower
  executeLoading: boolean
  isVinReady: boolean
  endTime: string
  totalEnergyWh: number
  onChangeStart: (value: string) => void
  onAddStep: () => void
  onRemoveStep: (index: number) => void
  onChangeStep: (index: number, field: keyof PlanStepDraft, value: string) => void
  onExecute: () => void
  onClose: () => void
}

export const ChargingPlanEditor: FC<ChargingPlanEditorProps> = ({
  start,
  steps,
  allowedChargingPower,
  executeLoading,
  isVinReady,
  endTime,
  totalEnergyWh,
  onChangeStart,
  onAddStep,
  onRemoveStep,
  onChangeStep,
  onExecute,
  onClose,
}) => {
  const allowedPowerLevels =
    allowedChargingPower && 'levels' in allowedChargingPower ? allowedChargingPower.levels : []
  const allowedPowerInterval =
    allowedChargingPower && 'interval' in allowedChargingPower
      ? allowedChargingPower.interval
      : undefined

  return (
    <section className="charging-plan-editor-wrapper" aria-label="Charging plan">
      <div className="charging-plan-header-row">
        <h2 className="charging-plan-label">Start Time</h2>
        <input
          type="datetime-local"
          className="charging-plan-input date-time"
          value={start}
          onChange={(event) => onChangeStart(event.target.value)}
        />
      </div>

      <div className="charging-plan-sections">
        {steps.map((step, index) => (
          <div key={index} className="charging-plan-section-card">
            <div className="charging-plan-section-header">
              <span className="charging-plan-section-title">Section {index + 1}</span>
              {steps.length > 1 && (
                <Button
                  variant="secondary"
                  size="compact"
                  className="charging-plan-remove-button"
                  onClick={() => onRemoveStep(index)}
                  aria-label={`Remove Section ${index + 1}`}
                >
                  <img src={CloseIcon} alt="" className="charging-plan-remove-icon" />
                </Button>
              )}
            </div>

            <div className="charging-plan-field-row">
              <label className="charging-plan-field">
                <span>Duration (Minutes)</span>
                <input
                  type="number"
                  min={1}
                  className="charging-plan-input"
                  value={step.duration}
                  onChange={(event) => onChangeStep(index, 'duration', event.target.value)}
                />
              </label>
            </div>

            <div className="charging-plan-field-row">
              <label className="charging-plan-field">
                <span>Charge power (W)</span>
                <input
                  type="number"
                  min={allowedPowerInterval?.min ?? 0}
                  max={allowedPowerInterval?.max}
                  className="charging-plan-input"
                  value={step.chargePower}
                  list={allowedPowerLevels.length > 0 ? `charge-power-options-${index}` : undefined}
                  onChange={(event) => onChangeStep(index, 'chargePower', event.target.value)}
                />
                {allowedPowerLevels.length > 0 && (
                  <datalist id={`charge-power-options-${index}`}>
                    {allowedPowerLevels.map((powerLevel) => (
                      <option key={powerLevel} value={powerLevel} />
                    ))}
                  </datalist>
                )}
              </label>
            </div>
          </div>
        ))}
      </div>

      <p className="charging-plan-allowed-power">
        Allowed charging power: {formatAllowedChargingPower(allowedChargingPower)}
      </p>

      <button type="button" className="charging-plan-add-section" onClick={onAddStep}>
        Add section
      </button>

      <div className="charging-plan-summary">
        <span>End time: {endTime}</span>
        <span>Energy: {totalEnergyWh.toFixed(0)} Wh</span>
      </div>

      <div className="charging-plan-submit-row">
        <Button variant="primary" onClick={onExecute} disabled={!isVinReady || executeLoading}>
          {executeLoading ? <LoadingIndicator style="INLINE" /> : 'Execute Charging Plan'}
        </Button>
      </div>
      <div className="charging-plan-close-row">
        <Button variant="secondary" onClick={onClose}>
          Close Charging Plan
        </Button>
      </div>
    </section>
  )
}
